// Locales the dashboard UI is translated into. Mirror of
// `backend/src/config/interfaceLanguages.ts` — the two packages don't share an
// import boundary, so keep the two lists in sync.
//
// This is a different axis from the voice language registry: a voice locale is
// gated on STT/TTS vendor coverage, an interface locale on a translated
// message catalogue under `src/i18n/locales/<code>/`. `catalogCompleteness`
// test enforces that every code here has one.

export interface InterfaceLanguageDefinition {
    code: string;
    label: string;
    nativeLabel: string;
    dir: "ltr" | "rtl";
}

export const DEFAULT_INTERFACE_LANGUAGE = "en";

/** Sentinel for a member who wants whatever the organization chose. */
export const INHERIT_INTERFACE_LANGUAGE = "inherit";

export const INTERFACE_LANGUAGES: readonly InterfaceLanguageDefinition[] = [
    { code: "en", label: "English", nativeLabel: "English", dir: "ltr" },
    { code: "es", label: "Spanish", nativeLabel: "Español", dir: "ltr" },
    { code: "fr", label: "French", nativeLabel: "Français", dir: "ltr" },
    { code: "de", label: "German", nativeLabel: "Deutsch", dir: "ltr" },
    { code: "pt-BR", label: "Portuguese (Brazil)", nativeLabel: "Português (Brasil)", dir: "ltr" },
    { code: "it", label: "Italian", nativeLabel: "Italiano", dir: "ltr" },
    { code: "hi", label: "Hindi", nativeLabel: "हिन्दी", dir: "ltr" },
    { code: "zh-Hans", label: "Chinese (Simplified)", nativeLabel: "简体中文", dir: "ltr" },
    { code: "ja", label: "Japanese", nativeLabel: "日本語", dir: "ltr" },
    { code: "ru", label: "Russian", nativeLabel: "Русский", dir: "ltr" },
];

export const INTERFACE_LANGUAGE_CODES: readonly string[] = INTERFACE_LANGUAGES.map((l) => l.code);

export function interfaceLanguage(code: string | null | undefined): InterfaceLanguageDefinition | undefined {
    if (!code) return undefined;
    const raw = String(code).trim();
    return INTERFACE_LANGUAGES.find((l) => l.code.toLowerCase() === raw.toLowerCase());
}

export function isSupportedInterfaceLanguage(code: string | null | undefined): boolean {
    return Boolean(interfaceLanguage(code));
}

export function normalizeInterfaceLanguage(code: string | null | undefined): string | null {
    return interfaceLanguage(code)?.code ?? null;
}

export function labelForInterfaceLanguage(code: string | null | undefined): string {
    const def = interfaceLanguage(code);
    return def ? def.nativeLabel : String(code ?? "");
}

export function directionForInterfaceLanguage(code: string | null | undefined): "ltr" | "rtl" {
    return interfaceLanguage(code)?.dir ?? "ltr";
}

/**
 * Best supported match for a browser tag, widening on the way: `pt-PT` has no
 * catalogue but `pt-BR` does, and `zh-CN` should land on `zh-Hans`. Returns
 * null rather than guessing when nothing in the family ships.
 */
export function matchInterfaceLanguage(tag: string | null | undefined): string | null {
    const raw = String(tag ?? "").trim();
    if (!raw) return null;

    const exact = normalizeInterfaceLanguage(raw);
    if (exact) return exact;

    const primary = raw.split("-")[0]!.toLowerCase();
    const inFamily = INTERFACE_LANGUAGES.find((l) => l.code.split("-")[0]!.toLowerCase() === primary);
    return inFamily?.code ?? null;
}

/**
 * The locale the SPA should render in. First concrete answer wins: the
 * member's own override, then their workspace's default, then whatever the
 * browser asks for, then English. `inherit` and unsupported stored values are
 * skipped rather than failing, so pulling a catalogue can't break a session.
 */
export function resolveInterfaceLanguage(sources: {
    userPreference?: string | null;
    organizationDefault?: string | null;
    browserLanguages?: readonly string[];
}): string {
    const { userPreference, organizationDefault, browserLanguages = [] } = sources;

    if (userPreference && userPreference !== INHERIT_INTERFACE_LANGUAGE) {
        const own = normalizeInterfaceLanguage(userPreference);
        if (own) return own;
    }

    const org = normalizeInterfaceLanguage(organizationDefault);
    if (org) return org;

    for (const tag of browserLanguages) {
        const matched = matchInterfaceLanguage(tag);
        if (matched) return matched;
    }

    return DEFAULT_INTERFACE_LANGUAGE;
}

export const getBrowserLanguages = (): readonly string[] =>
    typeof navigator === "undefined" ? [] : [...(navigator.languages ?? []), navigator.language].filter(Boolean);

export default INTERFACE_LANGUAGES;
